import {AppFrame} from "@/app/AppFrame";
import {Grid2 as Grid, Grid2, Tooltip} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {ButtonEx} from "@/lib/components/buttons/ButtonEx";
import {DataTable} from "@/lib/components/DataTable";
import * as React from "react";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {CellContext, ColumnDef} from "@tanstack/react-table";
import {
    CompanyOverviewRowDTO,
    CompanySearchParamsDTO,
    CompanyStatus,
    SearchResultCompanyOverviewRowDTO
} from "@/api/data-contracts";
import {useDialogController} from "@/lib/dialog-controller";
import {CompanyInvitationDialog} from "@/pages/company-overview-page/CompanyInvitationDialog";
import {companyApi} from "@/boxhub-api";
import StringInputField from "@/lib/components/inputfields/string-input-field";
import MultiSelectInputField from "@/lib/components/inputfields/multi-select-input-field";
import {ensureArray, formatCountWithSubset as formatCountWithSubsetUtil} from "@/lib/utils";
import i18n from "@/i18n";
import {DateFormatter} from "@/lib/components/DateFormate";
import dayjs from "dayjs";
import {withAuthWrapperComponent} from "@/app/auth/WithAuthWrapperComponent";
import {useQueryState} from "@/lib/querystate";
import {useSnackbarEx} from "@/lib/snackbarex";
import {ShortCutHandler} from "@/lib/shortcut-handler";
import {AppPaths} from "@/app/AppPaths";
import {useTranslation} from "react-i18next";
import {AdminI18N, CommonI18N, CompanyI18N, NotificationI18N} from "@/translations";
import {CompanyStatusBadge} from "@/common/CompanyStatusBadge";
import {useNavigate} from "react-router-dom";
import {companyStatusOptions} from "@/datacaches";
import EmailHistoryDialog from "@/common/EmailHistoryDialog";
import {useTheme} from "@mui/material/styles";
import {BriefIcon, PersonPlusIcon} from "@/agentur/AgenturIcons";
import DomainAddIcon from "@mui/icons-material/DomainAdd";
import {CreateTenantDialog} from "@/pages/company-overview-page/CreateTenantDialog";
import {companyStatusEventText} from "@/pages/company-overview-page/companyStatusEventText";

const CompanyLastLoginCell: React.FC<{ info: CellContext<CompanyOverviewRowDTO, unknown> }> = ({ info }) => {
    const row = info.row.original;
    const dateformat = DateFormatter("date_hm");
    if (!row.lastLoginAt) {
        return null;
    }
    const formatted = dayjs(row.lastLoginAt).format(dateformat);
    return (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            {formatted}
            <Tooltip title={row.lastLoginUserName ?? ""}>
                <InfoOutlinedIcon fontSize="small" color="action" />
            </Tooltip>
        </span>
    );
};

const CompanyStatusDateCell: React.FC<{ info: CellContext<CompanyOverviewRowDTO, unknown> }> = ({ info }) => {
    const row = info.row.original;
    const { t } = useTranslation();
    const dateOnlyFormat = DateFormatter("date");
    // The column already formats the status date in its accessorFn, which is the one source for the cell text.
    const formatted = (info.getValue() as string) ?? "";
    const lines = useMemo(
        () =>
            // Every event kind yields at most one line, so its type is a stable key.
            (row.statusEvents ?? [])
                .map((event) => ({ type: event.type, text: companyStatusEventText(event, t, dateOnlyFormat) }))
                .filter((line) => line.text.length > 0),
        [row.statusEvents, t, dateOnlyFormat]
    );
    return (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            {formatted}
            {lines.length > 0 && (
                <Tooltip
                    enterTouchDelay={0}
                    title={
                        <>
                            {lines.map((line) => (
                                <div key={line.type}>{line.text}</div>
                            ))}
                        </>
                    }
                >
                    <InfoOutlinedIcon fontSize="small" color="action" />
                </Tooltip>
            )}
        </span>
    );
};

const CompanyStatusCell: React.FC<{ info: CellContext<CompanyOverviewRowDTO, unknown> }> = ({ info }) => (
    <CompanyStatusBadge status={info.row.original.company?.status} />
);

const CompanyRequestsCell: React.FC<{ info: CellContext<CompanyOverviewRowDTO, unknown> }> = ({ info }) => {
    const { t } = useTranslation();
    return (
        <Tooltip title={t(AdminI18N.companies.column.requestsTooltip)} enterTouchDelay={0}>
            <span style={{ display: "block", textAlign: "right" }}>{info.getValue() as string}</span>
        </Tooltip>
    );
};

const CompanyQuotesCell: React.FC<{ info: CellContext<CompanyOverviewRowDTO, unknown> }> = ({ info }) => {
    const { t } = useTranslation();
    return (
        <Tooltip title={t(AdminI18N.companies.column.quotesTooltip)} enterTouchDelay={0}>
            <span style={{ display: "block", textAlign: "right" }}>{info.getValue() as string}</span>
        </Tooltip>
    );
};

const renderCompanyLastLoginCell = (info: CellContext<CompanyOverviewRowDTO, unknown>) => (
    <CompanyLastLoginCell info={info} />
);

const renderCompanyStatusDateCell = (info: CellContext<CompanyOverviewRowDTO, unknown>) => (
    <CompanyStatusDateCell info={info} />
);

const renderCompanyStatusCell = (info: CellContext<CompanyOverviewRowDTO, unknown>) => (
    <CompanyStatusCell info={info} />
);

const renderCompanyRequestsCell = (info: CellContext<CompanyOverviewRowDTO, unknown>) => (
    <CompanyRequestsCell info={info} />
);

const renderCompanyQuotesCell = (info: CellContext<CompanyOverviewRowDTO, unknown>) => (
    <CompanyQuotesCell info={info} />
);

export interface CompanyOverviewPageProps {
    unverified?: boolean;
}

const CompanyOverviewPage = ({ unverified }: CompanyOverviewPageProps) => {
    const [Anker, showDialog] = useDialogController();
    const { t } = useTranslation();
    const locale = i18n.language;
    const dateformat = DateFormatter("date_hm");
    const dateOnlyFormat = DateFormatter("date");
    const navigate = useNavigate();
    const { showMessage } = useSnackbarEx();
    const theme = useTheme();
    const [result, setResult] = useState<SearchResultCompanyOverviewRowDTO>({
        rows: []
    });

    // ModelState for search criteria (form fields)
    const { searchParams, onChangeSearchParams, getSearchParams, searchParamTrigger } =
        useQueryState<CompanySearchParamsDTO>({
            initialState: {
                status: unverified ? undefined : [CompanyStatus.CONFIRMED],
                companyName: ""
            },
            enhancer: {
                status: ensureArray
            },
            localStorageName: unverified
                ? ":company-unverfified-page-search-params"
                : ":company-overview-page-search-params"
        });

    // Status-Optionen
    const companyStatusSelectOptions = companyStatusOptions.map((option) => ({
        label: option && t(CommonI18N.enum.companyStatus[option]),
        value: option
    }));

    const formatCountWithSubset = useCallback(
        (total: number | undefined, subset: number | undefined) => formatCountWithSubsetUtil(total, subset, locale),
        [locale]
    );

    const columns = useMemo<ColumnDef<CompanyOverviewRowDTO>[]>(
        () => [
            {
                accessorKey: "name",
                header: t(AdminI18N.companies.column.name),
                accessorFn: (row) => row.company?.name
            },
            {
                accessorKey: "tenantName",
                header: t(AdminI18N.companies.column.tenant),
                accessorFn: (row) => row.tenantName ?? ""
            },
            {
                accessorKey: "address.city",
                header: t(CompanyI18N.form.field.city),
                accessorFn: (row) => row.company?.companyAddress?.city
            },
            {
                accessorKey: "address.country",
                header: t(AdminI18N.companies.column.country),
                accessorFn: (row) => {
                    const country = row.company?.companyAddress?.country;
                    return country ? t(CommonI18N.enum.country[country]) : "";
                }
            },
            {
                accessorKey: "lastLoginAt",
                header: t(AdminI18N.companies.column.lastActivity),
                meta: { disableTooltip: true },
                accessorFn: (row) => (row.lastLoginAt ? dayjs(row.lastLoginAt).format(dateformat) : ""),
                cell: renderCompanyLastLoginCell
            },
            {
                accessorKey: "status",
                header: t(AdminI18N.companies.column.status),
                accessorFn: (row) => row.company?.status && t(CommonI18N.enum.companyStatus[row.company?.status]),
                cell: renderCompanyStatusCell
            },
            {
                accessorKey: "statusDate",
                header: t(AdminI18N.companies.column.statusDate),
                meta: { disableTooltip: true },
                accessorFn: (row) => (row.statusDate ? dayjs(row.statusDate).format(dateOnlyFormat) : ""),
                cell: renderCompanyStatusDateCell
            },
            {
                accessorKey: "countOfBidderInteractions",
                header: t(AdminI18N.companies.label.bidderInteractions),
                meta: { disableTooltip: true },
                accessorFn: (row) => formatCountWithSubset(row.countOfBidderInteractions, row.countOfBidderRejections),
                cell: (info) => (
                    <Tooltip title={t(AdminI18N.companies.column.bidderInteractionsTooltip)} enterTouchDelay={0}>
                        <span style={{ display: "block", textAlign: "right" }}>{info.getValue() as string}</span>
                    </Tooltip>
                )
            },
            {
                accessorKey: "countOfPublishedRequests",
                header: t(AdminI18N.companies.label.requests),
                meta: { disableTooltip: true },
                accessorFn: (row) => formatCountWithSubset(row.countOfPublishedRequests, row.countOfConfirmedRequests),
                cell: renderCompanyRequestsCell
            },
            {
                accessorKey: "countOfPublishedQuotes",
                header: t(AdminI18N.companies.label.quotes),
                meta: { disableTooltip: true },
                accessorFn: (row) => formatCountWithSubset(row.countOfPublishedQuotes, row.countOfAcceptedQuotes),
                cell: renderCompanyQuotesCell
            }
        ],
        [dateformat, dateOnlyFormat, formatCountWithSubset, t]
    );

    const handleInviteCompany = () => {
        showDialog((open, close) => <CompanyInvitationDialog open={open} close={close} />);
    };

    const handleCreateTenant = () => {
        showDialog((open, close) => <CreateTenantDialog open={open} close={close} />);
    };

    // Identifies the most recently started search. Both trigger paths (debounce effect and the
    // Enter shortcut) share this single counter: a search remembers the id it was started with
    // and only applies its result or error once it completes, provided no newer search has
    // started in the meantime.
    const searchRequestIdRef = useRef(0);

    const handleSearch = useCallback(async () => {
        searchRequestIdRef.current += 1;
        const requestId = searchRequestIdRef.current;
        const sp = { ...getSearchParams() };
        if (unverified) {
            sp.status = [CompanyStatus.UNVERIFIED];
        }
        try {
            const response = await companyApi.searchCompanies(sp);
            if (requestId === searchRequestIdRef.current) {
                setResult(response.data);
            }
        } catch (error: unknown) {
            if (requestId === searchRequestIdRef.current) {
                showMessage({ summary: t(AdminI18N.companies.message.searchFailed), error, severity: "error" });
            }
        }
    }, [getSearchParams, showMessage, t, unverified]);

    const handleOpenEmailHistoryDialog = () => {
        showDialog((open, close) => <EmailHistoryDialog open={open} onClose={close} />);
    };

    useEffect(() => {
        const timeoutHandle = setTimeout(() => {
            void handleSearch();
        }, 250);
        return () => {
            clearTimeout(timeoutHandle);
            // Also retires a search that is already in flight: bumping the counter makes its captured id
            // stale, so neither its result nor its error snackbar is applied after this effect run is over —
            // whether that is a newer filter superseding it or the page being left altogether.
            searchRequestIdRef.current += 1;
        };
    }, [handleSearch, onChangeSearchParams, searchParamTrigger]);

    const buttons = (
        <Grid2 container direction="row" spacing={2}>
            <Grid>
                <ButtonEx
                    label={t(NotificationI18N.emailHistory.action.open)}
                    startIcon={<BriefIcon mode={theme.palette.mode} />}
                    data-cy="email-history-button"
                    onClick={handleOpenEmailHistoryDialog}
                    color="primary"
                />
            </Grid>
            <Grid2>
                <ButtonEx
                    label={t(AdminI18N.companies.action.createTenant)}
                    color="secondary"
                    startIcon={<DomainAddIcon />}
                    onClick={handleCreateTenant}
                />
            </Grid2>
            <Grid2>
                <ButtonEx
                    label={t(AdminI18N.companies.action.invite)}
                    color="secondary"
                    startIcon={<PersonPlusIcon mode={theme.palette.mode} />}
                    onClick={handleInviteCompany}
                />
            </Grid2>
        </Grid2>
    );

    const shortcuts = {
        Enter: () => void handleSearch()
    };

    return (
        <AppFrame
            title={t(unverified ? AdminI18N.companies.page.registrationRequestsTitle : AdminI18N.companies.page.title)}
            buttons={buttons}
            padding={2}
        >
            <ShortCutHandler shortcuts={shortcuts}>
                <Grid2 container direction="column" spacing={4} paddingTop={1}>
                    <Grid2 container direction="row" spacing={2}>
                        {unverified ? null : (
                            <Grid2 size={{ sm: 12, md: 8 }}>
                                <MultiSelectInputField
                                    name="status"
                                    label={t(AdminI18N.companies.column.status)}
                                    options={companyStatusSelectOptions}
                                    value={searchParams.status ?? []}
                                    onChange={onChangeSearchParams}
                                    renderOption={(option) => (
                                        <CompanyStatusBadge key={option.value} status={option.value} />
                                    )}
                                    disabled={unverified}
                                />
                            </Grid2>
                        )}
                        <Grid2 size={{ sm: 12, md: 4 }}>
                            <StringInputField
                                name="companyName"
                                label={t(AdminI18N.companies.column.name)}
                                value={searchParams.companyName ?? ""}
                                onChange={onChangeSearchParams}
                            />
                        </Grid2>
                    </Grid2>

                    <Grid2 overflow="auto" width="100%">
                        <DataTable
                            columns={columns}
                            data={result?.rows ?? []}
                            noResultsValue={t(AdminI18N.companies.message.notFound)}
                            onRowClick={(row) => {
                                navigate(AppPaths.COMPANY_fn(row.id!));
                            }}
                            striped
                        />
                    </Grid2>
                </Grid2>
                <Anker />
            </ShortCutHandler>
        </AppFrame>
    );
};

export default withAuthWrapperComponent(CompanyOverviewPage);
